import { HttpCriteria } from '@app/core/http';

import { Column, IColumn } from './column';
import { IViews, Views } from './views';

interface ITable {
  columnId: string;
  columns: IColumn[];
  /**
   * @default null
   */
  label?: string;
  name: string;
  /**
   * @default null
   */
  url?: string;
  /**
   * @optional
   */
  views?: IViews;
}

const DEFAULT = {
  label: null,
  url: null,
  views: {} as IViews
};

class Table {
  private criteria: HttpCriteria = new HttpCriteria({ _perPage: -1 });
  protected columnId: string;
  protected columns: Column[];
  protected label: string;
  protected name: string;
  protected url: string;
  protected views: Views;

  constructor(o: ITable) {
    this.columnId = o.columnId;
    this.columns = o.columns.map(_ => new Column(_));
    this.label = o.label || DEFAULT.label;
    this.name = o.name;
    this.url = o.url || DEFAULT.url;
    this.views = new Views(
      o.views || DEFAULT.views,
      this.columns.map(_ => _.getName())
    );
  }

  /**
   * @alias clone (non static)
   * @returns A new instance of `Table` with the same values
   */
  public static clone(o: Table): Table {
    return o.clone();
  }

  /**
   * @returns A new instance of `Table` with the same values
   */
  public clone(): Table {
    return new Table(this.toObject());
  }

  public getColumnByName(name: string): Column {
    return this.columns.find(_ => _.getName() === name);
  }

  public getColumnId(): Column {
    return this.getColumnByName(this.columnId);
  }

  public getColumns(): Column[] {
    return this.columns;
  }

  public getColumnsByView(
    viewName: 'create' | 'detail' | 'read' | 'update'
  ): Column[] {
    let columns = [];
    switch (viewName) {
      case 'create':
        columns = this.views.getCanCreate();
        break;
      case 'detail':
        columns = this.views.getCanDetail();
        break;
      case 'read':
        columns = this.views.getCanRead();
        break;
      case 'update':
        columns = this.views.getCanUpdate();
        break;
    }

    return columns.map(_ => this.getColumnByName(_));
  }

  public getColumnsToEdit(): Column[] {
    return this.columns.filter(_ => _.getIsEditable());
  }

  public getColumnsToFilter(): Column[] {
    return this.columns.filter(_ => _.getIsFilterable());
  }

  public getColumnsToSort(): Column[] {
    return this.columns.filter(_ => _.getIsSortable());
  }

  public getCriteria(): HttpCriteria {
    return this.criteria;
  }

  public getLabel(): string {
    return this.label || this.name;
  }

  public getName(): string {
    return this.name;
  }

  public getUrl(): string {
    return this.url || `/${this.name}`;
  }

  public getViews(): Views {
    return this.views;
  }

  /**
   * @returns The current *table* as plain object
   */
  public toObject(): ITable {
    return {
      columnId: this.columnId,
      columns: this.columns.map(_ => _.toObject()),
      label: this.label,
      name: this.name,
      url: this.url,
      views: this.views.toObject()
    };
  }

  /**
   * @returns The current *table* as string
   */
  public toString(): string {
    return JSON.stringify(this.toObject());
  }
}

export { ITable, Table };
